import React from 'react';
import { List, Typography } from 'antd';
import { NotificationOutlined, TrophyFilled, ThunderboltFilled } from '@ant-design/icons'

const { Text } = Typography;

function formatTime(seconds) {
    let reverse = false
    if (seconds < 0) {
        seconds = -seconds
        reverse = true
    }
    const minutes = Math.floor(seconds / 60); // Get the full minutes
    const remainingSeconds = seconds % 60;    // Get the remaining seconds
    // Return the formatted time string
    // Add a leading zero to seconds if less than 10
    const timeStr = reverse ? `-${minutes}'${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}` : `${minutes}'${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
    return timeStr
}

// Create a translucent variant of an rgba() color
function withAlpha(rgbaColor, alpha) {
    const match = rgbaColor.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([0-9.]+))?\s*\)/i);
    if (!match) return rgbaColor;
    const r = parseInt(match[1], 10);
    const g = parseInt(match[2], 10);
    const b = parseInt(match[3], 10);
    const a = typeof alpha === 'number' ? alpha : (match[4] ? parseFloat(match[4]) : 1);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
}

function HomeTrainerProgress({ steps, FTP, currentIndex, isMobile }) {
    const getColor = (intensity) => {
        if (intensity >= FTP * 1.5) {
            return 'rgba(128, 0, 128, 1)';
        } else if (intensity >= FTP * 1.19) {
            return 'rgba(255, 0, 0, 1)';
        } else if (intensity >= FTP * 1.05) {
            return 'rgba(255, 165, 0, 1)';
        } else if (intensity >= FTP * 0.9) {
            return 'rgba(255, 215, 0, 1)';
        } else if (intensity >= FTP * 0.75) {
            return 'rgba(0, 128, 0, 1)';
        }
        else if (intensity > FTP * 0.6) {
            return 'rgba(153, 204, 255, 1)';
        } else {
            return 'rgba(128, 128, 128, 1)';
        }
    };

    const isWorkoutDone = Array.isArray(steps) && steps.length > 0 && currentIndex >= steps.length - 1;

    const GlassBadge = ({ icon, title, subtitle, accent = "rgba(255, 215, 0, 1)", children }) => (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: 16,
                textAlign: "center",
            }}
        >
            <div
                style={{
                    width: isMobile ? "100%" : 420,
                    maxWidth: "100%",
                    borderRadius: 18,
                    padding: isMobile ? "16px 14px" : "18px 16px",
                    background: `linear-gradient(180deg, ${withAlpha(accent, 0.22)}, rgba(255,255,255,0.18))`,
                    border: `1px solid ${withAlpha(accent, 0.45)}`,
                    backdropFilter: "blur(14px)",
                    WebkitBackdropFilter: "blur(14px)",
                    boxShadow: `0 10px 30px ${withAlpha(accent, 0.18)}, 0 10px 26px rgba(0,0,0,0.10)`,
                }}
            >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 6 }}>
                    <span
                        style={{
                            width: 34,
                            height: 34,
                            borderRadius: 12,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: `linear-gradient(180deg, ${withAlpha(accent, 0.45)}, ${withAlpha(accent, 0.18)})`,
                            border: `1px solid ${withAlpha(accent, 0.55)}`,
                            boxShadow: `0 8px 18px ${withAlpha(accent, 0.18)}`,
                            color: "rgba(0,0,0,0.78)",
                            fontSize: 18,
                        }}
                    >
                        {icon}
                    </span>
                    <Text strong style={{ fontSize: 16, color: "rgba(0,0,0,0.82)" }}>
                        {title}
                    </Text>
                </div>
                {subtitle && (
                    <Text type="secondary" style={{ display: "block", marginBottom: children ? 10 : 0 }}>
                        {subtitle}
                    </Text>
                )}
                {children}
            </div>
        </div>
    );

    // Empty state when no steps loaded
    if (!steps || steps.length === 0) {
        return (
            <GlassBadge
                icon={<ThunderboltFilled />}
                title={"Free Ride"}
                subtitle={isMobile ? "No plan loaded. Ride freely." : "No plan loaded. Ride freely or load a workout."}
                accent={"rgba(153, 204, 255, 1)"}
            />
        );
    }

    return (
        <div>
            {currentIndex < steps.length - 1 && (
                <div
                    style={{
                        maxHeight: isMobile ? "55vh" : "43vh",
                        overflowY: "auto",
                        paddingRight: 6,
                    }}
                >
                    <List
                            bordered={false}
                            split={false}
                            dataSource={steps.slice(currentIndex + 1)}
                            renderItem={(step, index) => {
                                const base = getColor(step.watt);
                                const tintStrong = withAlpha(base, index === 0 ? 0.25 : 0.16);
                                const tintSoft = withAlpha(base, 0.06);
                                const borderCol = withAlpha(base, index === 0 ? 0.5 : 0.35);
                                const glow = index === 0 ? `0 6px 22px ${withAlpha(base, 0.32)}` : "0 2px 12px rgba(0,0,0,0.12)";
                                return (
                                    <List.Item
                                        style={{
                                            background: `linear-gradient(180deg, ${tintStrong}, ${tintSoft})`,
                                            backdropFilter: "blur(12px)",
                                            WebkitBackdropFilter: "blur(12px)",
                                            border: `1px solid ${borderCol}`,
                                            borderRadius: 14,
                                            boxShadow: glow,
                                            margin: "8px 6px",
                                            padding: "10px 12px",
                                            transition: "transform .18s ease, box-shadow .18s ease",
                                        }}
                                    >
                                        <div style={{ display: "flex", alignItems: "center", width: "100%", gap: 10 }}>
                                            <div
                                                style={{
                                                    width: 8,
                                                    alignSelf: "stretch",
                                                    borderRadius: 8,
                                                    background: withAlpha(base, index === 0 ? 0.7 : 0.5),
                                                }}
                                            />
                                            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "rgba(0,0,0,0.75)", flexWrap: "wrap" }}>
                                                {index === 0 ? (
                                                    <b><NotificationOutlined /></b>
                                                ) : (
                                                    <Text type="secondary" style={{ minWidth: 18, textAlign: "right" }}>
                                                        {index + currentIndex + 2}.
                                                    </Text>
                                                )}
                                                {index === 0 && (
                                                    <b style={{ color: "rgba(0,0,0,0.78)" }}>
                                                        {index + currentIndex + 2}.
                                                    </b>
                                                )}
                                                <Text strong>{formatTime(step.duration)}</Text>
                                                <span>at</span>
                                                <Text strong>{Math.round(step.watt)} {isMobile ? "W" : "Watts"}</Text>
                                                {!isMobile && step.comment && (
                                                    <span style={{ color: "rgba(0,0,0,0.55)" }}>{step.comment}</span>
                                                )}
                                            </div>
                                        </div>
                                    </List.Item>
                                );
                            }}
                        />
                </div>
            )}

            {isWorkoutDone && (
                <GlassBadge
                    icon={<TrophyFilled />}
                    title={"Workout Complete"}
                    subtitle={isMobile ? "Nice work. Cool down and hydrate." : "Nice work. You’ve finished this session."}
                    accent={"rgba(255, 215, 0, 1)"}
                />
            )}

            {/* Finish logo display section - shows a centered finish logo with responsive sizing based on workout progress 
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "150px", // Fixed height container for the finish logo
                }}
            >
                <img
                    src={logo_finish}
                    alt={"Finish"}
                    style={{
                        // Responsive sizing: smaller during workout, larger when approaching/at finish
                        maxWidth: currentIndex < steps.length - 1 ? "40%" : currentIndex === steps.length - 1 ? "50%" : "60%",
                        objectFit: "contain", // Ensures the image scales proportionally within container
                    }}
                />
            </div>*/}

        </div>
    );

}

export default HomeTrainerProgress;
